import { createSlice, current } from "@reduxjs/toolkit";
import { arrayMove } from "utils/helpers";
import { isSystemCustomField, omitSystemCustomFieldColumn } from "utils/custom-fields";

const initialBoardPagination = {
  pageSize: 50
};

function computeStageHasMore(incomingCount, pageSize, loadedCount, stageCount) {
  if (incomingCount < pageSize) {
    return false;
  }
  if (stageCount != null && loadedCount >= stageCount) {
    return false;
  }
  return incomingCount > 0;
}

function getColumnByStageId(columns, stageId) {
  return columns[stageId] ?? columns[String(stageId)];
}

function countStageItems(tableData, stageId) {
  return tableData.filter((item) => String(item?.stage_id) === String(stageId)).length;
}

function opportunityMatchesFilters(item, filters) {
  const tenantUserMatch = filters.tenant_user_id === "no_filter" || item.tenant_user_id === filters.tenant_user_id;
  const currencyMatch = filters.currency_id == null || item.currency_id === filters.currency_id;
  const statusMatch =
    filters.status_ids === "no_filter" ||
    (Array.isArray(filters.status_ids) && filters.status_ids.includes(item.status_id));
  return tenantUserMatch && currencyMatch && statusMatch;
}

function toBoardItem(item, filters) {
  return {
    ...item,
    cost: parseFloat(item.cost) || 0,
    revenue: parseFloat(item.revenue) || 0,
    probability: parseFloat(item.probability) || 0,
    id: item.opportunity_id,
    hidden: !opportunityMatchesFilters(item, filters)
  };
}

function stageSummaryRows(payload) {
  const rows = payload?.StageSummary || payload?.stageSummary || payload?.stage_summary;
  return Array.isArray(rows) ? rows : null;
}

function applyStageSummary(columns, rows) {
  if (!rows) {
    return;
  }
  rows.forEach((row) => {
    const column = columns[row.stage_id] ?? columns[String(row.stage_id)];
    if (!column) {
      return;
    }
    column.stageCount = parseInt(row.stage_count, 10) || 0;
    column.stageRevenue = parseFloat(row.stage_revenue) || 0;
  });
}

function adjustStageSummary(column, countDelta, revenueDelta) {
  if (!column || column.stageCount == null) {
    return;
  }
  column.stageCount = Math.max(0, column.stageCount + countDelta);
  if (column.stageRevenue != null) {
    column.stageRevenue = Math.max(0, (parseFloat(column.stageRevenue) || 0) + (revenueDelta || 0));
  }
}

function syncColumnStagePagination(column, tableData, stageId, pageSize, currentPage) {
  if (!column) {
    return;
  }
  const incomingCount = countStageItems(tableData, stageId);
  column.currentPage = currentPage;
  column.hasMore = computeStageHasMore(incomingCount, pageSize, column.itemIds.length, column.stageCount);
  column.isLoadingMore = false;
}

export const opportunitySettingsSlice = createSlice({
  name: "opportunity",
  initialState: {
    isFetched: false,
    opportunities: {
      items: {},
      columns: {},
      columnsOrder: [],
      stageSummary: []
    },
    filters: {
      tenant_user_id: "no_filter",
      currency_id: null,
      status_ids: "no_filter"
    },
    filtersFormInvalid: false,

    isSingleFetched: false,
    isLoading: false,
    opportunity: undefined,

    status: {
      isFetched: false,
      isLoading: false,
      list: [],
      columnNames: [],
      attributeData: {
        isFetched: false,
        list: []
      }
    },
    type: {
      isFetched: false,
      isLoading: false,
      list: [],
      columnNames: [],
      attributeData: {
        isFetched: false,
        list: []
      }
    },
    stage: {
      isFetched: false,
      isLoading: false,
      list: [],
      columnNames: [],
      attributeData: {
        isFetched: false,
        list: []
      }
    },

    board: {
      isFetched: false,
      isLoading: false,
      board_id_in_use: null,
      list: [],
      columnNames: [],
      attributeData: {
        isFetched: false,
        list: []
      }
    },
    boardPagination: initialBoardPagination,

    customField: {
      isFetched: false,
      isLoading: false,
      list: [],
      columnNames: [],
      isSingleFetched: false,
      singleCustomField: undefined
    },
    customFieldAttributes: {
      isFetched: false,
      list: []
    }
  },
  reducers: {
    onCallOpportunities: (state, { payload }) => {
      // payload -> without loading

      if (!payload) {
        state.isFetched = false;
        state.opportunities = {
          items: {},
          columns: {},
          columnsOrder: [],
          stageSummary: []
        };
      }
    },
    onSuccessOpportunities: (state, { payload }) => {
      const tableData = payload.tableData || [];
      const opportunity_board_id = state.board.attributeData.list.find((board) => board.isDefault === true)?.code;
      const columnsOrder = state.stage.attributeData.list
        .filter((stage) => stage?.board_id === (payload.boardId || opportunity_board_id))
        .map((stage) => ({
          id: stage.code,
          name: stage.name
        }));
      state.board.board_id_in_use = payload.boardId || opportunity_board_id;
      const pageSize = payload.pageSize || initialBoardPagination.pageSize;
      const currentPage = payload.currentPage || 1;

      if (payload.append && payload.stageId != null) {
        const stageId = payload.stageId;
        const stageItems = (payload.tableData || []).filter(
          (item) => String(item?.stage_id) === String(stageId)
        );

        stageItems.forEach((item) => {
          if (state.opportunities.items[item.opportunity_id]) {
            return;
          }
          state.opportunities.items[item.opportunity_id] = toBoardItem(item, state.filters);
          const column = getColumnByStageId(state.opportunities.columns, stageId);
          if (column && !column.itemIds.includes(item.opportunity_id)) {
            column.itemIds.push(item.opportunity_id);
          }
        });

        syncColumnStagePagination(
          getColumnByStageId(state.opportunities.columns, stageId),
          stageItems,
          stageId,
          pageSize,
          currentPage
        );
      } else {
        const items = {};
        const columns = {};

        tableData.forEach((item) => {
          items[item.opportunity_id] = toBoardItem(item, state.filters);
        });

        columnsOrder.forEach((column) => {
          const itemIds = tableData.filter((item) => item?.stage_id === column.id).map((item) => item.opportunity_id);
          columns[column.id] = {
            id: column.id,
            title: column.name,
            color: state.stage.attributeData.list.find((stage) => stage.code === column.id).color,
            itemIds,
            currentPage: 1,
            hasMore: false,
            isLoadingMore: false
          };
        });

        state.opportunities = {
          items,
          columns,
          columnsOrder: columnsOrder.map((c) => c.id),
          stageSummary: state.opportunities.stageSummary || []
        };
      }

      const summaryRows = stageSummaryRows(payload);
      if (summaryRows) {
        state.opportunities.stageSummary = summaryRows;
        applyStageSummary(state.opportunities.columns, summaryRows);
      }

      if (!payload.append || payload.stageId == null) {
        columnsOrder.forEach((column) => {
          syncColumnStagePagination(
            getColumnByStageId(state.opportunities.columns, column.id),
            tableData,
            column.id,
            pageSize,
            1
          );
        });
      }

      state.isFetched = true;
      state.boardPagination = {
        pageSize
      };
    },
    onFailedOpportunities: (state) => {
      state.isFetched = true;
      state.opportunities = {
        items: {},
        columns: {},
        columnsOrder: [],
        stageSummary: []
      };
      state.boardPagination = initialBoardPagination;
    },
    setOpportunitiesStageLoadingMore: (state, { payload: { stageId, status } }) => {
      const column = getColumnByStageId(state.opportunities.columns, stageId);
      if (column) {
        column.isLoadingMore = Boolean(status);
      }
    },

    switchOpportunitiesLoading: (state, { payload }) => {
      state.isLoading = payload !== undefined ? payload : !state.isLoading;
    },

    onSuccessOpportunity: (state, { payload }) => {
      state.isSingleFetched = true;
      state.opportunity = payload;
    },
    onFailedOpportunity: (state) => {
      state.isSingleFetched = true;
      state.opportunity = undefined;
    },
    resetOpportunity: (state) => {
      state.isSingleFetched = false;
      state.opportunity = undefined;
    },

    onSuccessOpportunityAdd: (state, { payload }) => {
      if (payload) {
        const tenantUserMatch =
          state.filters.tenant_user_id === "no_filter" || payload.tenant_user_id === state.filters.tenant_user_id;
        const currencyMatch = state.filters.currency_id == null || payload.currency_id === state.filters.currency_id;
        const statusMatch =
          state.filters.status_ids === "no_filter" ||
          (Array.isArray(state.filters.status_ids) && state.filters.status_ids.includes(payload.status_id));
        state.opportunities.items[payload.opportunity_id] = {
          ...payload,
          cost: parseFloat(payload.cost) || 0,
          revenue: parseFloat(payload.revenue) || 0,
          probability: parseFloat(payload.probability) || 0,
          id: payload.opportunity_id,
          hidden: !(tenantUserMatch && currencyMatch && statusMatch)
        };

        const columnId = payload?.stage_id;
        // const columnName = payload?.stage;
        // if (state.opportunities.columns[columnId]) {
        state.opportunities.columns[columnId].itemIds.push(payload.opportunity_id);
        adjustStageSummary(
          state.opportunities.columns[columnId],
          1,
          parseFloat(payload.revenue) || 0
        );
        // } else {
        //     state.opportunities.columnsOrder.push(columnId);
        //     state.opportunities.columns[columnId] = {
        //         id: columnId,
        //         title: columnName,
        //         itemIds: [payload.opportunity_id]
        //     };
        // }
      }
    },
    onSuccessOpportunityEdit: (state, { payload }) => {
      if (payload) {
        const tenantUserMatch =
          state.filters.tenant_user_id === "no_filter" || payload.tenant_user_id === state.filters.tenant_user_id;
        const currencyMatch = state.filters.currency_id == null || payload.currency_id === state.filters.currency_id;
        const statusMatch =
          state.filters.status_ids === "no_filter" ||
          (Array.isArray(state.filters.status_ids) && state.filters.status_ids.includes(payload.status_id));
        const newOpportunity = {
          ...payload,
          cost: parseFloat(payload.cost) || 0,
          revenue: parseFloat(payload.revenue) || 0,
          probability: parseFloat(payload.probability) || 0,
          id: payload.opportunity_id,
          hidden: !(tenantUserMatch && currencyMatch && statusMatch)
        };

        const oldOpportunity = state.opportunities.items[payload.opportunity_id];
        // state.opportunities.items[payload.opportunity_id] = {
        //     ...payload,
        //     // stage: opp.stage,
        //     // stage_id: opp.stage_id,
        //     // position: opp.position,
        //     cost: parseFloat(payload.cost),
        //     revenue: parseFloat(payload.revenue)
        //     // id: payload.opportunity_id
        // };

        const newStageId = newOpportunity.stage_id;
        const oldStageId = oldOpportunity.stage_id;
        const oldRevenue = parseFloat(oldOpportunity?.revenue) || 0;
        const newRevenue = parseFloat(newOpportunity.revenue) || 0;

        if (newStageId !== oldStageId) {
          adjustStageSummary(state.opportunities.columns[oldStageId], -1, -oldRevenue);
          adjustStageSummary(state.opportunities.columns[newStageId], 1, newRevenue);
        } else if (oldRevenue !== newRevenue) {
          adjustStageSummary(state.opportunities.columns[oldStageId], 0, newRevenue - oldRevenue);
        }

        const newPosition = newOpportunity.position;
        const oldPosition = oldOpportunity.position;

        if (!(newStageId in state.opportunities.columns)) {
          state.opportunities.columns[oldStageId].itemIds = state.opportunities.columns[oldStageId].itemIds.filter(
            (item) => item !== newOpportunity.opportunity_id
          );
        } else if (newStageId !== oldStageId) {
          // let positionIsChanged = false;
          // if (newPosition !== oldPosition) {
          //     positionIsChanged = true;
          // }

          // state.opportunities.columns[oldStageId] = {
          //     ...state.opportunities.columns[oldStageId],
          //     itemIds: state.opportunities.columns[oldStageId].itemIds.filter(i => i !== payload.opportunity_id)
          // };
          // state.opportunities.columns[oldStageId].itemIds.filter(itemId => itemId !== payload.opportunity_id);
          state.opportunities.columns[oldStageId].itemIds.splice(oldPosition, 1);

          // const newItems = [...state.opportunities.columns[newStageId].itemIds];
          // state.opportunities.columns[newStageId] = {
          //     ...state.opportunities.columns[newStageId],
          //     itemIds: [...newItems.splice(newPosition, 0, payload.opportunity_id)]
          // };

          state.opportunities.columns[newStageId].itemIds.splice(newPosition, 0, payload.opportunity_id);
        } else if (newPosition !== oldPosition) {
          // state.opportunities.columns[oldStageId].itemIds.splice(oldPosition, 1);
          // state.opportunities.columns[oldStageId].itemIds.splice(newPosition, 0, payload.opportunity_id);
          state.opportunities.columns[oldStageId].itemIds = arrayMove(
            state.opportunities.columns[oldStageId].itemIds,
            oldPosition,
            newPosition
          );
        }

        state.opportunities.items[payload.opportunity_id] = newOpportunity;
      }
    },
    onSuccessOpportunitiesDelete: (state, { payload: { opportunityIds, newStageColumns, opportunityPositions } }) => {
      if (opportunityIds !== undefined) {
        opportunityIds.forEach((id) => {
          const item = state.opportunities.items[id];
          if (!item) {
            return;
          }
          adjustStageSummary(newStageColumns[item.stage_id], -1, -(parseFloat(item.revenue) || 0));
        });
        state.opportunities.columns = newStageColumns;

        // Object.keys(state.opportunities.columns)
        //     .forEach(columnKey => {
        //         state.opportunities.columns[columnKey].itemIds = state.opportunities.columns[columnKey].itemIds.filter(i => !opportunityIds.includes(i));
        //     });

        Object.keys(state.opportunities.items).forEach((itemKey) => {
          if (opportunityIds.includes(itemKey)) {
            delete state.opportunities.items[itemKey];
          }
        });

        opportunityPositions.forEach((item) => {
          state.opportunities.items[item.opportunity_id].position = item.position;
        });
      }
    },
    onSuccessOpportunitiesPositionsChange: (state, { payload: { opportunityPositions, stageId } }) => {
      if (stageId !== undefined) {
        state.opportunities.columns[stageId].itemIds = opportunityPositions.map((item) => item.opportunity_id);

        opportunityPositions.forEach((item) => {
          state.opportunities.items[item.opportunity_id].position = item.position;
        });
      }
    },
    onSuccessOpportunityStageChange: (state, { payload }) => {
      if (payload !== undefined) {
        const { fromStageId, toStageId, fromStagePositions, toStagePositions, opportunityId, probability } = payload;

        state.opportunities.columns[fromStageId].itemIds = fromStagePositions.map((item) => item.opportunity_id);
        state.opportunities.columns[toStageId].itemIds = toStagePositions.map((item) => item.opportunity_id);
        const movedRevenue = parseFloat(state.opportunities.items[opportunityId]?.revenue) || 0;
        adjustStageSummary(state.opportunities.columns[fromStageId], -1, -movedRevenue);
        adjustStageSummary(state.opportunities.columns[toStageId], 1, movedRevenue);
        state.opportunities.items[opportunityId].stage_id = toStageId;

        if (probability > 0) {
          state.opportunities.items[opportunityId].probability = probability;
        }

        fromStagePositions.forEach((item) => {
          state.opportunities.items[item.opportunity_id].position = item.position;
        });
        toStagePositions.forEach((item) => {
          state.opportunities.items[item.opportunity_id].position = item.position;
        });
      }
    },

    onCallOpportunitySettings: (state, { payload }) => {
      state[payload.key].isFetched = false;
      state[payload.key].list = [];
      state[payload.key].columnNames = [];
    },
    onSuccessOpportunitySettings: (state, { payload }) => {
      state[payload.key].isFetched = true;
      state[payload.key].list = payload.data.tableData;
      state[payload.key].columnNames = payload.data.columnNames;
    },
    onFailedOpportunitySettings: (state, { payload }) => {
      state[payload.key].isFetched = true;
      state[payload.key].list = [];
      state[payload.key].columnNames = [];
    },

    switchOpportunitySettingLoading: (state, { payload }) => {
      state[payload.key].isLoading =
        payload[payload.key] !== undefined ? payload[payload.key] : !state[payload.key].isLoading;
    },

    onSuccessOpportunitySettingAdd: (state, { payload }) => {
      if (payload.data) {
        if (payload.data.Default) {
          state[payload.key].list = [
            ...state[payload.key].list.map((item) => ({
              ...item,
              Default: false
            })),
            payload.data
          ];
        } else {
          state[payload.key].list = [...state[payload.key].list, payload.data];
        }
      }
    },
    onSuccessOpportunitySettingEdit: (state, { payload }) => {
      if (payload.data) {
        if (payload.data.Default) {
          state[payload.key].list = state[payload.key].list.map((item) =>
            item[payload.idKey] === payload.data[payload.idKey]
              ? payload.data
              : {
                  ...item,
                  Default: false
                }
          );
        } else {
          state[payload.key].list = state[payload.key].list.map((item) =>
            item[payload.idKey] === payload.data[payload.idKey] ? payload.data : item
          );
        }
      }
    },
    onSuccessOpportunitySettingDelete: (state, { payload }) => {
      if (payload.id !== undefined) {
        if (payload.key === "stage") {
          const deletedItem = state[payload.key].list.reduce((acc, item) => {
            if (item[payload.idKey] === payload.id) {
              acc = { position: item["List Position"], boardName: item.Board };
            }
            return acc;
          }, {});
          state[payload.key].list = state[payload.key].list.reduce((acc, item) => {
            if (item["List Position"] > deletedItem.position && item.Board === deletedItem.boardName) {
              item["List Position"] -= 1;
            }
            if (item[payload.idKey] !== payload.id) {
              acc.push(item);
            }
            return acc;
          }, []);
        } else {
          state[payload.key].list = state[payload.key].list.filter((item) => item[payload.idKey] !== payload.id);
        }
      }
    },
    onReorderOpportunitySetting: (state, { payload: { key, idKey, data } }) => {
      // state[payload.key].list = payload.data;
      if (key === "stage") {
        state[key].list = state[key].list.map((stage) => data.find((item) => item[idKey] === stage[idKey]) || stage);
      } else {
        state[key].list = data;
      }
    },
    onFailedReorderOpportunitySetting: (state, { payload }) => {
      state[payload.key].list = payload.data;
    },

    // ATTRIBUTES
    onCallOpportunityAttributes: (state, { payload }) => {
      state[payload.key].attributeData.isFetched = false;
      state[payload.key].attributeData.isLoading = false;
      state[payload.key].attributeData.list = [];
    },
    onSuccessOpportunityAttributes: (state, { payload }) => {
      state[payload.key].attributeData.isFetched = true;

      if (payload?.data?.tableData?.[0]) {
        const codeFieldName = Object.keys(payload.data.tableData[0]).find((k) => k.includes("_id"));
        const nameFieldName = Object.keys(payload.data.tableData[0]).find(
          (k) => !k.includes("Default") && !k.includes("_id")
        );
        state[payload.key].attributeData.list = payload.data.tableData.map((item) => ({
          code: item[codeFieldName],
          name: item[nameFieldName],
          isDefault: item.default_value,
          ...(item.color !== undefined && { color: item.color }),
          ...(item.board_id !== undefined && { board_id: item.board_id }),
          ...(item.position !== undefined && { position: item.position }),
          ...(item.probability !== undefined && { probability: item.probability })
        }));
      }
    },
    onFailedOpportunityAttributes: (state, { payload }) => {
      state[payload.key].attributeData.isFetched = true;
      state[payload.key].attributeData.list = [];
    },
    onSuccessOpportunityAttributeAdd: (state, { payload }) => {
      if (payload.data) {
        const codeFieldName = Object.keys(payload.data).find((k) => k.includes("_id"));
        const nameFieldName = Object.keys(payload.data).find((k) => !k.includes("Default") && !k.includes("_id"));
        state[payload.key].attributeData.list = [
          ...state[payload.key].attributeData.list,
          {
            code: payload.data[codeFieldName],
            name: payload.key === "stage" ? payload.data.Stage : payload.data[nameFieldName],
            isDefault: payload.data.Default,
            ...(payload.data.Board && {
              board_id: state.board.attributeData.list.find((item) => item.name === payload.data.Board)?.code
            })
          }
        ];

        // In case if adding stage from dropdown update stage list in columns (real time update needed)
        if (payload.key === "stage") {
          const newStageBoardCode =
            payload.data.Board &&
            state.board.attributeData.list.find((item) => item.name === payload.data.Board)?.code;
          if (newStageBoardCode === state.board.board_id_in_use) {
            const stageId = payload.data.opportunity_stage_id;
            const stagePosition = payload.data["List Position"];
            const stageTitle = payload.data.Stage;
            const stageColor = payload.data.Color;
            state.opportunities.columnsOrder.splice(stagePosition, 0, stageId);
            state.opportunities.columns[stageId] = {
              id: stageId,
              title: stageTitle,
              color: stageColor,
              itemIds: []
            };
          }
        }
      }
    },
    onSuccessOpportunityAttributeEdit: (state, { payload }) => {
      if (payload.data) {
        const nameFieldName = Object.keys(payload.data).find((k) => !k.includes("Default") && !k.includes("_id"));
        if (payload.data.Default) {
          state[payload.key].attributeData.list = state[payload.key].attributeData.list.map((item) =>
            item.code === payload.data[payload.idKey]
              ? {
                  code: payload.data[payload.idKey],
                  name: payload.key === "stage" ? payload.data.Stage : payload.data[nameFieldName],
                  isDefault: payload.data.Default,
                  ...(payload.data["List Position"] && { position: payload.data["List Position"] }),
                  ...(payload.data.Color && { color: payload.data.Color }),
                  ...(payload.data.Probability && { probability: payload.data.Probability }),
                  ...(payload.data.Board && {
                    board_id: state.board.attributeData.list.find((item) => item.name === payload.data.Board)?.code
                  })
                }
              : {
                  ...item,
                  isDefault: false
                }
          );
        } else {
          state[payload.key].attributeData.list = state[payload.key].attributeData.list.map((item) =>
            item.code === payload.data[payload.idKey]
              ? {
                  code: payload.data[payload.idKey],
                  name: payload.key === "stage" ? payload.data.Stage : payload.data[nameFieldName],
                  isDefault: payload.data.Default,
                  ...(payload.data["List Position"] && { position: payload.data["List Position"] }),
                  ...(payload.data.Color && { color: payload.data.Color }),
                  ...(payload.data.Probability && { probability: payload.data.Probability }),
                  ...(payload.data.Board && {
                    board_id: state.board.attributeData.list.find((item) => item.name === payload.data.Board)?.code
                  })
                }
              : item
          );
        }
      }
    },
    switchOpportunitiesAttributeChangeLoading: (state, { payload }) => {
      state[payload.key].attributeData.isLoading =
        payload[payload.key] !== undefined ? payload[payload.key] : !state[payload.key].attributeData.isLoading;
    },

    // FILTERS
    setOpportunitiesFiltersValidity: (state, { payload }) => {
      const invalid = payload === false;
      state.filtersFormInvalid = invalid;
      if (invalid) {
        Object.keys(state.opportunities.items).forEach((id) => {
          state.opportunities.items[id].hidden = true;
        });
      }
    },
    setOpportunitiesFilter: (state, { payload }) => {
      const { key, value } = payload;
      if (key === "tenant_user_id") {
        state.filters.tenant_user_id = value;
      } else if (key === "currency_id") {
        state.filters.currency_id = value;
      } else if (key === "status_ids") {
        state.filters.status_ids = value === "no_filter" ? "no_filter" : Array.isArray(value) ? value : [];
      }

      const { tenant_user_id, currency_id, status_ids } = state.filters;

      const updatedItems = {};
      const hideAll = state.filtersFormInvalid;

      Object.values(current(state.opportunities.items)).forEach((opp) => {
        const hidden = hideAll
          ? true
          : !(
              (tenant_user_id === "no_filter" || opp.tenant_user_id === tenant_user_id) &&
              (currency_id == null || opp.currency_id === currency_id) &&
              (status_ids === "no_filter" || (Array.isArray(status_ids) && status_ids.includes(opp.status_id)))
            );
        updatedItems[opp.opportunity_id] = { ...opp, hidden };
      });


      state.opportunities.items = updatedItems;
    },
    changeActiveBoard: (state, { payload }) => {
      state.board.board_id_in_use = payload;
    },

    onCallOpportunityCustomFields: (state) => {
      state.customField.isFetched = false;
      state.customField.isLoading = false;
      state.customField.list = [];
      state.customField.columnNames = [];
    },
    onSuccessOpportunityCustomFields: (state, { payload }) => {
      state.customField.isFetched = true;
      state.customField.list = payload.tableData;
      state.customField.columnNames = omitSystemCustomFieldColumn(payload.columnNames);
    },
    onFailedOpportunityCustomFields: (state) => {
      state.customField.isFetched = true;
      state.customField.list = [];
      state.customField.columnNames = [];
    },
    switchOpportunityCustomFieldLoading: (state, { payload }) => {
      state.customField.isLoading = payload !== undefined ? payload : !state.customField.isLoading;
    },
    onSuccessOpportunityCustomField: (state, { payload }) => {
      state.customField.isSingleFetched = true;
      state.customField.singleCustomField = payload;
    },
    onFailedOpportunityCustomField: (state) => {
      state.customField.isSingleFetched = true;
      state.customField.singleCustomField = undefined;
    },
    resetOpportunityCustomField: (state) => {
      state.customField.isSingleFetched = false;
      state.customField.singleCustomField = undefined;
    },
    onSuccessOpportunityCustomFieldAdd: (state, { payload }) => {
      if (payload) {
        state.customField.list = [...state.customField.list, payload];
      }
    },
    onSuccessOpportunityCustomFieldEdit: (state, { payload }) => {
      if (payload) {
        state.customField.list = state.customField.list.map((customField) =>
          customField.custom_field_definition_id === payload.custom_field_definition_id ? payload : customField
        );
      }
    },
    onSuccessOpportunityCustomFieldDelete: (state, { payload }) => {
      if (payload !== undefined) {
        const deletedItemIndex = state.customField.list.findIndex(
          (customField) => customField.custom_field_definition_id === payload
        );
        state.customField.list = state.customField.list.reduce((acc, item, index) => {
          if (index >= deletedItemIndex && deletedItemIndex !== -1) {
            item.Position -= 1;
          }
          if (item.custom_field_definition_id !== payload) {
            acc.push(item);
          }
          return acc;
        }, []);
        state.customFieldAttributes.list = state.customFieldAttributes.list.filter(
          (item) => item.custom_field_definition_id !== payload
        );
      }
    },
    onReorderOpportunityCustomFields: (state, { payload }) => {
      state[payload.key].list = payload.data;
    },
    onFailedReorderOpportunityCustomFields: (state, { payload }) => {
      state[payload.key].list = payload.data;
    },

    onCallOpportunityCustomFieldAttributes: (state) => {
      state.customFieldAttributes.isFetched = false;
      state.customFieldAttributes.list = [];
    },
    onSuccessOpportunityCustomFieldAttributes: (state, { payload }) => {
      state.customFieldAttributes.isFetched = true;

      if (payload?.custom_fields) {
        state.customFieldAttributes.list = payload.custom_fields
          .filter(
            (item) =>
              (item.visible || isSystemCustomField(item)) && !Number.isInteger(item.custom_field_definition_parent_id)
          )
          .map((item) => {
            if (item.list) {
              const children = (item.list_items || []).map((child) => ({
                ...child,
                default_value: payload.custom_fields.find(
                  (field) => field.custom_field_definition_id === child.custom_field_definition_id
                )?.default_value
              }));
              return {
                ...item,
                list_items: children
              };
            }

            return item;
          })
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      }
    },
    onFailedOpportunityCustomFieldAttributes: (state) => {
      state.customFieldAttributes.isFetched = true;
      state.customFieldAttributes.list = [];
    },
    onReorderMainOpportunityCustomFields: (state, { payload }) => {
      if (state.customFieldAttributes.list.length > 0) {
        state.customFieldAttributes.list = state.customFieldAttributes.list.map((item) => {
          const findCustomField = payload?.data?.find(
            (customField) => customField?.custom_field_definition_id === item?.custom_field_definition_id
          );
          if (findCustomField) {
            return {
              ...item,
              position: findCustomField.Position
            };
          }
          return item;
        });
        state.customFieldAttributes.list = [...state.customFieldAttributes.list].sort((a, b) =>
          a.position > b.position ? 1 : -1
        );
      }
    }
  }
});

export const {
  onCallOpportunities,
  onSuccessOpportunities,
  onFailedOpportunities,
  setOpportunitiesStageLoadingMore,
  switchOpportunitiesLoading,
  onSuccessOpportunity,
  onFailedOpportunity,
  resetOpportunity,

  onSuccessOpportunityAdd,
  onSuccessOpportunityEdit,
  onSuccessOpportunitiesDelete,
  onSuccessOpportunitiesPositionsChange,
  onSuccessOpportunityStageChange,

  onCallOpportunitySettings,
  onSuccessOpportunitySettings,
  onFailedOpportunitySettings,
  switchOpportunitySettingLoading,
  onSuccessOpportunitySettingAdd,
  onSuccessOpportunitySettingEdit,
  onSuccessOpportunitySettingDelete,

  onCallOpportunityAttributes,
  onSuccessOpportunityAttributes,
  onFailedOpportunityAttributes,
  onSuccessOpportunityAttributeAdd,
  onSuccessOpportunityAttributeEdit,
  switchOpportunitiesAttributeChangeLoading,

  setOpportunitiesFiltersValidity,
  setOpportunitiesFilter,
  filterOpportunitiesByBoard,

  onReorderOpportunitySetting,
  onFailedReorderOpportunitySetting,

  onSocketOpportunitiesPositionsChange,

  changeActiveBoard,

  onCallOpportunityCustomFields,
  onSuccessOpportunityCustomFields,
  onFailedOpportunityCustomFields,
  switchOpportunityCustomFieldLoading,
  onSuccessOpportunityCustomField,
  onFailedOpportunityCustomField,
  resetOpportunityCustomField,
  onSuccessOpportunityCustomFieldAdd,
  onSuccessOpportunityCustomFieldEdit,
  onSuccessOpportunityCustomFieldDelete,
  onReorderOpportunityCustomFields,
  onFailedReorderOpportunityCustomFields,
  onCallOpportunityCustomFieldAttributes,
  onSuccessOpportunityCustomFieldAttributes,
  onFailedOpportunityCustomFieldAttributes,
  onReorderMainOpportunityCustomFields
} = opportunitySettingsSlice.actions;

export default opportunitySettingsSlice.reducer;
